import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Grid,
  Button,
  IconButton
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Fingerprint as FingerprintIcon,
  Link as LinkIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';

const formatDate = (date) => {
  if (!date) return 'N/A';
  const dateObj = new Date(date);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[dateObj.getMonth()];
  const day = dateObj.getDate();
  let hours = dateObj.getHours();
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  return `${month} ${day}, ${hours}:${minutes} ${ampm}`;
};

const AgentCard = ({ agent, onDelete }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(agent);
  };

  const handleManageAgent = () => {
    navigate(`/agents/${agent.id}/manage`);
  };

  return (
    <Card
      sx={{
        height: '100%',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-6px) scale(1.03)',
          boxShadow: theme.shadows[6],
        },
        position: 'relative',
        borderRadius: 4,
        boxShadow: theme.shadows[2],
        border: `1.5px solid ${theme.palette.divider}`,
        minHeight: 360,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        background: theme.palette.background.paper
      }}
    >
      <CardContent sx={{ p: 3, pb: '24px!important', position: 'relative' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700, 
              fontSize: '1.18rem', 
              color: theme.palette.text.primary, 
              maxWidth: '70%', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap' 
            }}
          >
            {agent.name}
          </Typography>
          <IconButton
            size="small"
            color="error"
            onClick={handleDelete}
            sx={{ ml: 0.5 }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1, fontWeight: 500 }}>
            AI Agent
          </Typography>
          <Chip
            label={agent.isActive ? 'ACTIVE' : 'INACTIVE'}
            color={agent.isActive ? 'success' : 'warning'}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 22,
              borderRadius: 2,
              letterSpacing: 0.5,
              bgcolor: agent.isActive ? theme.palette.primary.main : '#ff9800',
              color: agent.isActive ? 'black' : 'white',
              '& .MuiChip-label': {
                px: 1.2
              }
            }}
          />
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <FingerprintIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.primary.main, opacity: 0.8 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Agent ID
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: theme.palette.text.primary, wordBreak: 'break-all' }}>
              {agent.id}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LinkIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.primary.main, opacity: 0.8 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Endpoint
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: theme.palette.text.primary, wordBreak: 'break-all' }}>
              {agent.endpoint}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.primary.main, opacity: 0.8 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Created
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
              {formatDate(agent.createdAt)}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.primary.main, opacity: 0.8 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Updated
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
              {formatDate(agent.updatedAt)}
            </Typography>
          </Grid>
        </Grid>

        <Button
          variant="contained"
          fullWidth
          onClick={handleManageAgent}
          sx={{
            mt: 2,
            py: 1.2,
            fontSize: '1rem',
            fontWeight: 700,
            borderRadius: 3,
            bgcolor: theme.palette.primary.main,
            boxShadow: theme.shadows[2],
            letterSpacing: 0.5,
            '&:hover': { bgcolor: theme.palette.primary.dark },
            transition: 'all 0.2s',
          }}
        >
          Manage Agent
        </Button>
      </CardContent>
    </Card>
  );
};

AgentCard.propTypes = {
  agent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    endpoint: PropTypes.string.isRequired,
    isActive: PropTypes.bool.isRequired,
    createdAt: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired
  }).isRequired,
  onDelete: PropTypes.func.isRequired
};

export default AgentCard; 